import React, { useState, useEffect, useMemo } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FiAlertTriangle, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { CgPlug } from 'react-icons/cg';
import toast from 'react-hot-toast';

import { DataState } from '../../context/DataProvider';
import { useMCP } from '../../context/McpProvider';
import { getAppResources, API_URL } from '../../services/api';
import * as features from '../../config/features';
const { ENABLE_MCP } = features;

// ---------------------------------------------------------------------------
// Shared toggle switch
// ---------------------------------------------------------------------------
const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:ring-offset-2 focus:ring-offset-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? 'bg-blue-600' : 'bg-zinc-700'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

// ---------------------------------------------------------------------------
// Thin labelled divider
// ---------------------------------------------------------------------------
const SectionDivider = ({ label }) => (
  <div className="flex items-center gap-2 py-2">
    <div className="h-px flex-1 bg-zinc-700" />
    {label && (
      <span className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">
        {label}
      </span>
    )}
    <div className="h-px flex-1 bg-zinc-700" />
  </div>
);

// ---------------------------------------------------------------------------
// JIRA inline config
// ---------------------------------------------------------------------------
const JiraInlineConfig = ({ userGoogleId }) => {
  const { appConfigs, setAppConfigs } = useMCP();
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedProjectKeys, setSelectedProjectKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const processData = (data) => {
    setResources(data.resources || []);
    if (data.selected_resource) {
      const r = data.selected_resource;
      setSelectedResource(r);
      setSelectedProjectKeys(
        new Set(r.selected_projects?.map((p) => p.key) || []),
      );
    }
    setLastRefreshed(data.timestamp);
  };

  useEffect(() => {
    if (appConfigs?.jira) {
      processData(appConfigs.jira);
      setLoading(false);
    } else {
      getAppResources('jira', userGoogleId)
        .then((data) => {
          if (setAppConfigs) setAppConfigs((p) => ({ ...p, jira: data }));
          processData(data);
        })
        .catch(() => toast.error('Failed to fetch Jira resources.'))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/auth/jira/resources/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (res.status !== 200) throw new Error(data.detail || 'Failed');
      if (setAppConfigs) setAppConfigs((p) => ({ ...p, jira: data }));
      processData(data);
      toast.success('Refreshed!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    if (!selectedResource) return toast.error('Select a resource first.');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/auth/jira/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        body: JSON.stringify({
          selected_resource_id: selectedResource.id,
          project_keys: Array.from(selectedProjectKeys),
        }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to save.');
      toast.success('Jira configuration saved!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="py-2 text-xs text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-2">
      {/* header */}
      <div className="flex items-center justify-between">
        {lastRefreshed && (
          <span className="text-[10px] text-zinc-500">
            Updated:{' '}
            {new Date(
              isNaN(new Date(lastRefreshed))
                ? lastRefreshed + 'Z'
                : lastRefreshed,
            ).toLocaleString()}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1 text-zinc-400 hover:text-white disabled:opacity-50"
            title="Refresh"
          >
            <FiRefreshCw
              size={12}
              className={isRefreshing ? 'animate-spin' : ''}
            />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedResource}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* resources */}
      {resources.map((resource) => {
        const isSelected = selectedResource?.id === resource.id;
        return (
          <div
            key={resource.id}
            className={`rounded border ${isSelected ? 'border-[#5652CF]' : 'border-zinc-700'}`}
          >
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-zinc-700/30">
              <input
                type="radio"
                name="jira-resource"
                checked={isSelected}
                onChange={() => {
                  setSelectedResource(resource);
                  setSelectedProjectKeys(new Set());
                }}
                className="form-radio h-3.5 w-3.5 text-[#5652CF]"
              />
              {resource.avatarUrl && (
                <img
                  src={resource.avatarUrl}
                  alt={resource.name}
                  className="h-5 w-5 rounded-full"
                />
              )}
              <span className="text-sm text-zinc-200">{resource.name}</span>
            </label>
            <div className="border-t border-zinc-700 bg-zinc-800/40 px-3 py-2">
              {!isSelected && (
                <p className="mb-1 text-[10px] text-yellow-500">
                  Select this resource to pick projects.
                </p>
              )}
              <ul
                className={`space-y-1 ${!isSelected ? 'pointer-events-none opacity-40' : ''}`}
              >
                {resource.projects.map((project) => (
                  <li
                    key={project.key}
                    className="flex items-center gap-2 px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      id={`jira-proj-${project.key}`}
                      checked={selectedProjectKeys.has(project.key)}
                      onChange={() => {
                        const s = new Set(selectedProjectKeys);
                        s.has(project.key)
                          ? s.delete(project.key)
                          : s.add(project.key);
                        setSelectedProjectKeys(s);
                      }}
                      disabled={!isSelected}
                      className="form-checkbox h-3.5 w-3.5 text-[#5652CF] disabled:cursor-not-allowed"
                    />
                    <label
                      htmlFor={`jira-proj-${project.key}`}
                      className="cursor-pointer text-xs text-zinc-300"
                    >
                      {project.name}{' '}
                      <span className="text-zinc-500">({project.key})</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CONFLUENCE inline config
// ---------------------------------------------------------------------------
const ConfluenceInlineConfig = ({ userGoogleId }) => {
  const { appConfigs, setAppConfigs } = useMCP();
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [selectedSpaceKeys, setSelectedSpaceKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const processData = (data) => {
    setResources(data.resources || []);
    if (data.selected_resource) {
      const r = data.selected_resource;
      setSelectedResource(r);
      setSelectedSpaceKeys(new Set(r.selected_spaces?.map((s) => s.key) || []));
    }
    setLastRefreshed(data.timestamp);
  };

  useEffect(() => {
    if (appConfigs?.confluence) {
      processData(appConfigs.confluence);
      setLoading(false);
    } else {
      getAppResources('confluence', userGoogleId)
        .then((data) => {
          if (setAppConfigs) setAppConfigs((p) => ({ ...p, confluence: data }));
          processData(data);
        })
        .catch(() => toast.error('Failed to fetch Confluence resources.'))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/auth/confluence/resources/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (res.status !== 200) throw new Error(data.detail || 'Failed');
      if (setAppConfigs) setAppConfigs((p) => ({ ...p, confluence: data }));
      processData(data);
      toast.success('Refreshed!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    if (!selectedResource) return toast.error('Select a resource first.');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/auth/confluence/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        body: JSON.stringify({
          selected_resource_id: selectedResource.id,
          space_keys: Array.from(selectedSpaceKeys),
        }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to save.');
      toast.success('Confluence configuration saved!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="py-2 text-xs text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        {lastRefreshed && (
          <span className="text-[10px] text-zinc-500">
            Updated:{' '}
            {new Date(
              isNaN(new Date(lastRefreshed))
                ? lastRefreshed + 'Z'
                : lastRefreshed,
            ).toLocaleString()}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1 text-zinc-400 hover:text-white disabled:opacity-50"
            title="Refresh"
          >
            <FiRefreshCw
              size={12}
              className={isRefreshing ? 'animate-spin' : ''}
            />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !selectedResource}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      {resources.map((resource) => {
        const isSelected = selectedResource?.id === resource.id;
        return (
          <div
            key={resource.id}
            className={`rounded border ${isSelected ? 'border-[#5652CF]' : 'border-zinc-700'}`}
          >
            <label className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-zinc-700/30">
              <input
                type="radio"
                name="conf-resource"
                checked={isSelected}
                onChange={() => {
                  setSelectedResource(resource);
                  setSelectedSpaceKeys(new Set());
                }}
                className="form-radio h-3.5 w-3.5 text-[#5652CF]"
              />
              {resource.avatarUrl && (
                <img
                  src={resource.avatarUrl}
                  alt={resource.name}
                  className="h-5 w-5 rounded-full"
                />
              )}
              <span className="text-sm text-zinc-200">{resource.name}</span>
            </label>
            <div className="border-t border-zinc-700 bg-zinc-800/40 px-3 py-2">
              {!isSelected && (
                <p className="mb-1 text-[10px] text-yellow-500">
                  Select this resource to pick spaces.
                </p>
              )}
              <ul
                className={`space-y-1 ${!isSelected ? 'pointer-events-none opacity-40' : ''}`}
              >
                {resource.spaces.map((space) => (
                  <li
                    key={space.key}
                    className="flex items-center gap-2 px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      id={`conf-space-${space.key}`}
                      checked={selectedSpaceKeys.has(space.key)}
                      onChange={() => {
                        const s = new Set(selectedSpaceKeys);
                        s.has(space.key)
                          ? s.delete(space.key)
                          : s.add(space.key);
                        setSelectedSpaceKeys(s);
                      }}
                      disabled={!isSelected}
                      className="form-checkbox h-3.5 w-3.5 text-[#5652CF] disabled:cursor-not-allowed"
                    />
                    <label
                      htmlFor={`conf-space-${space.key}`}
                      className="cursor-pointer text-xs text-zinc-300"
                    >
                      {space.name}{' '}
                      <span className="text-zinc-500">({space.key})</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MICROSOFT TEAMS inline config
// ---------------------------------------------------------------------------
const TeamsInlineConfig = ({ userGoogleId }) => {
  const { appConfigs, setAppConfigs } = useMCP();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chats, setChats] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [selectedChannels, setSelectedChannels] = useState({});
  const [selectedChats, setSelectedChats] = useState(new Set());
  const [activeTab, setActiveTab] = useState('teams');
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const processData = (data) => {
    setTeams(data.teams || []);
    setChannels(data.channels || []);
    setChats(data.chats || []);
    setSelectedTeams(
      new Set(
        data.teams?.filter((t) => t.isSelected).map((t) => t.team_id) || [],
      ),
    );
    setSelectedChats(
      new Set(
        data.chats?.filter((c) => c.isSelected).map((c) => c.chat_id) || [],
      ),
    );
    const initChannels = {};
    data.channels?.forEach((c) => {
      if (c.isSelected) {
        if (!initChannels[c.team_id]) initChannels[c.team_id] = new Set();
        initChannels[c.team_id].add(c.channel_id);
      }
    });
    setSelectedChannels(initChannels);
    setLastRefreshed(data.timestamp);
  };

  useEffect(() => {
    if (appConfigs?.microsoft_teams) {
      processData(appConfigs.microsoft_teams);
      setLoading(false);
    } else {
      fetch(`${API_URL}/microsoft_teams/resources`, {
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success) {
            if (setAppConfigs)
              setAppConfigs((p) => ({ ...p, microsoft_teams: json.data }));
            processData(json.data);
          } else throw new Error(json.message);
        })
        .catch((e) => toast.error(e.message))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/microsoft_teams/resources/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      if (setAppConfigs)
        setAppConfigs((p) => ({ ...p, microsoft_teams: json.data }));
      processData(json.data);
      toast.success('Refreshed!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        teams: Array.from(selectedTeams),
        chats: Array.from(selectedChats),
        channels: Object.entries(selectedChannels).reduce(
          (acc, [tId, cSet]) => {
            if (cSet.size > 0) acc[tId] = Array.from(cSet);
            return acc;
          },
          {},
        ),
      };
      const res = await fetch(`${API_URL}/microsoft_teams/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      toast.success('Teams selections saved!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const channelsByTeam = useMemo(
    () =>
      channels.reduce((acc, c) => {
        if (!acc[c.team_id]) acc[c.team_id] = [];
        acc[c.team_id].push(c);
        return acc;
      }, {}),
    [channels],
  );

  if (loading) return <p className="py-2 text-xs text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex gap-3 text-xs">
          {['teams', 'channels', 'chats'].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`pb-0.5 capitalize ${activeTab === t ? 'border-b border-white text-white' : 'text-zinc-500'}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {lastRefreshed && (
            <span className="mr-2 text-[10px] text-zinc-500">
              Updated:{' '}
              {new Date(
                isNaN(new Date(lastRefreshed))
                  ? lastRefreshed + 'Z'
                  : lastRefreshed,
              ).toLocaleString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1 text-zinc-400 hover:text-white disabled:opacity-50"
          >
            <FiRefreshCw
              size={12}
              className={isRefreshing ? 'animate-spin' : ''}
            />
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="space-y-1">
        {activeTab === 'teams' &&
          teams.map((team) => (
            <label
              key={team.team_id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-700/30"
            >
              <input
                type="checkbox"
                checked={selectedTeams.has(team.team_id)}
                onChange={() => {
                  const s = new Set(selectedTeams);
                  if (s.has(team.team_id)) {
                    s.delete(team.team_id);
                    const nc = { ...selectedChannels };
                    delete nc[team.team_id];
                    setSelectedChannels(nc);
                  } else s.add(team.team_id);
                  setSelectedTeams(s);
                }}
                className="form-checkbox h-3.5 w-3.5 text-[#5652CF]"
              />
              <span className="text-xs text-zinc-300">{team.displayName}</span>
              {team.members && (
                <span className="ml-auto flex items-center gap-1 text-[10px] text-zinc-500">
                  <FiUsers size={10} />
                  {team.members.length}
                </span>
              )}
            </label>
          ))}

        {activeTab === 'channels' &&
          (selectedTeams.size === 0 ? (
            <p className="px-2 text-xs text-zinc-500">Select a team first.</p>
          ) : (
            teams
              .filter((t) => selectedTeams.has(t.team_id))
              .map((team) => (
                <div
                  key={team.team_id}
                  className="rounded border border-zinc-700"
                >
                  <div className="bg-zinc-800/30 px-3 py-2">
                    <span className="text-xs font-medium text-zinc-200">
                      {team.displayName}
                    </span>
                  </div>
                  <ul className="space-y-1 border-t border-zinc-700 px-3 py-2">
                    {(channelsByTeam[team.team_id] || []).map((ch) => (
                      <li key={ch.channel_id}>
                        <label className="flex cursor-pointer items-center gap-2 rounded py-1 hover:bg-zinc-700/30">
                          <input
                            type="checkbox"
                            checked={
                              selectedChannels[team.team_id]?.has(
                                ch.channel_id,
                              ) || false
                            }
                            onChange={() => {
                              const nc = { ...selectedChannels };
                              if (!nc[team.team_id])
                                nc[team.team_id] = new Set();
                              const s = new Set(nc[team.team_id]);
                              s.has(ch.channel_id)
                                ? s.delete(ch.channel_id)
                                : s.add(ch.channel_id);
                              nc[team.team_id] = s;
                              setSelectedChannels(nc);
                            }}
                            className="form-checkbox h-3.5 w-3.5 text-[#5652CF]"
                          />
                          <span className="text-xs text-zinc-300">
                            {ch.displayName}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
          ))}

        {activeTab === 'chats' &&
          chats.map((chat) => (
            <label
              key={chat.chat_id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-700/30"
            >
              <input
                type="checkbox"
                checked={selectedChats.has(chat.chat_id)}
                onChange={() => {
                  const s = new Set(selectedChats);
                  s.has(chat.chat_id)
                    ? s.delete(chat.chat_id)
                    : s.add(chat.chat_id);
                  setSelectedChats(s);
                }}
                className="form-checkbox h-3.5 w-3.5 text-[#5652CF]"
              />
              <span className="truncate text-xs text-zinc-300">
                {chat.topic ||
                  `Chat with ${chat.members?.map((m) => m.displayName).join(', ')}`}
              </span>
            </label>
          ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GOHIGHLEVEL inline config
// ---------------------------------------------------------------------------
const GohighlevelInlineConfig = ({ userGoogleId }) => {
  const { appConfigs, setAppConfigs } = useMCP();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [subaccountsList, setSubaccountsList] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const processData = (data) => {
    const acc = data.account_info;
    setAccountInfo(acc);
    setSubaccountsList(acc.subaccounts || []);
    setSelectedIds(new Set(acc.selectedSubaccounts || []));
  };

  useEffect(() => {
    if (appConfigs?.gohighlevel) {
      processData(appConfigs.gohighlevel);
      setLoading(false);
    } else {
      fetch(`${API_URL}/gohighlevel/resources`, {
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      })
        .then((r) => r.json())
        .then((json) => {
          if (json.success) {
            if (setAppConfigs)
              setAppConfigs((p) => ({ ...p, gohighlevel: json.data }));
            processData(json.data);
          } else throw new Error(json.message);
        })
        .catch((e) => toast.error(e.message))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/gohighlevel/resources/refresh`, {
        headers: { Authorization: `Bearer ${userGoogleId}` },
        credentials: 'include',
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      if (setAppConfigs)
        setAppConfigs((p) => ({ ...p, gohighlevel: json.data }));
      processData(json.data);
      toast.success('Refreshed!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/gohighlevel/resources/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        body: JSON.stringify({ selectedSubaccounts: Array.from(selectedIds) }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to save.');
      toast.success('Configuration saved!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(
    () =>
      subaccountsList.filter((s) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [subaccountsList, searchTerm],
  );

  if (loading) return <p className="py-2 text-xs text-zinc-500">Loading…</p>;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {accountInfo?.userType === 'Company' && (
          <input
            type="text"
            placeholder="Search subaccounts…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none"
          />
        )}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1 text-zinc-400 hover:text-white disabled:opacity-50"
        >
          <FiRefreshCw
            size={12}
            className={isRefreshing ? 'animate-spin' : ''}
          />
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {accountInfo?.userType === 'Company' ? (
        <div className="space-y-1">
          {filtered.map((sub) => (
            <label
              key={sub.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-zinc-700/30"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(sub.id)}
                onChange={() => {
                  const s = new Set(selectedIds);
                  s.has(sub.id) ? s.delete(sub.id) : s.add(sub.id);
                  setSelectedIds(s);
                }}
                className="form-checkbox h-3.5 w-3.5 text-[#5652CF]"
              />
              <span className="text-xs text-zinc-300">{sub.name}</span>
            </label>
          ))}
        </div>
      ) : (
        accountInfo && (
          <p className="text-xs text-zinc-400">
            Subaccount user — no sub-selection needed.
          </p>
        )
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Map appname → inline config component
// ---------------------------------------------------------------------------
const INLINE_CONFIGS = {
  jira: JiraInlineConfig,
  confluence: ConfluenceInlineConfig,
  microsoft_teams: TeamsInlineConfig,
  gohighlevel: GohighlevelInlineConfig,
};

// ---------------------------------------------------------------------------
// Main McpTaskSelector — NO collapse anywhere, everything always visible
// ---------------------------------------------------------------------------
const McpTaskSelector = ({ isOpen, onClose }) => {
  const { connections, oqlousTools, isLoading } = useMCP();

  const {
    taskMCPConnections,
    setTaskMCPConnections,
    taskOqlousTools,
    setTaskOqlousTools,
    taskManualToolSelection,
    setTaskManualToolSelection,
    userGoogleId,
  } = DataState();

  const [expandedMcpId, setExpandedMcpId] = useState(null);
  const [expandedAppName, setExpandedAppName] = useState(null);

  const {
    validConnectionsToShow,
    invalidatedConnectionsToShow,
    validOqlousAppsToShow,
    invalidatedOqlousAppsToShow,
  } = useMemo(() => {
    if (isLoading)
      return {
        validConnectionsToShow: [],
        invalidatedConnectionsToShow: [],
        validOqlousAppsToShow: [],
        invalidatedOqlousAppsToShow: [],
      };

    const invalidatedMcp = taskMCPConnections
      .map((taskConn) => {
        const g = connections.find((c) => c.id === taskConn.id);
        if (!g || g.connection_status !== 'valid')
          return {
            id: taskConn.id,
            connection_name: g?.connection_name || 'Unknown Connection',
            errorMessage:
              g?.error_message || 'This connection has been deleted.',
          };
        return null;
      })
      .filter(Boolean);
    const invalidatedMcpIds = new Set(invalidatedMcp.map((ic) => ic.id));
    const validMcp = connections.filter(
      (c) => c.connection_status === 'valid' && !invalidatedMcpIds.has(c.id),
    );

    const globalNames = new Set(oqlousTools.map((a) => a.appname));
    const invalidatedOqlous = taskOqlousTools
      .filter((ta) => !globalNames.has(ta.appname))
      .map((ta) => ({
        appname: ta.appname,
        errorMessage: `The "${ta.appname}" integration is no longer authenticated or available.`,
      }));
    const invalidatedOqlousNames = new Set(
      invalidatedOqlous.map((a) => a.appname),
    );
    const validOqlous = oqlousTools.filter((a) => {
      if (invalidatedOqlousNames.has(a.appname)) return false;

      // Normalize appname (e.g., 'zoho_crm' -> 'ZOHOCRM', 'go-high-level' -> 'GOHIGHLEVEL')
      const normalizedAppName = a.appname
        .replace(/[^A-Z0-9]/gi, '')
        .toUpperCase();

      // Find the corresponding feature flag key by ignoring underscores/hyphens
      const matchingFlagKey = Object.keys(features).find((key) => {
        if (!key.startsWith('ENABLE_INTEGRATION_')) return false;
        const normalizedKey = key
          .replace('ENABLE_INTEGRATION_', '')
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase();
        return normalizedKey === normalizedAppName;
      });

      if (matchingFlagKey) {
        return features[matchingFlagKey] !== false; // Hide only if explicitly set to false
      }

      // Safe fallback: If no matching feature flag exists, assume it's enabled
      return true;
    });

    return {
      validConnectionsToShow: validMcp,
      invalidatedConnectionsToShow: invalidatedMcp,
      validOqlousAppsToShow: validOqlous,
      invalidatedOqlousAppsToShow: invalidatedOqlous,
    };
  }, [
    connections,
    oqlousTools,
    taskMCPConnections,
    taskOqlousTools,
    isLoading,
  ]);

  const handleMcpConnectionToggle = (e, connection) => {
    e.stopPropagation();
    const isAdded = taskMCPConnections.some((tc) => tc.id === connection.id);
    if (isAdded) {
      setTaskMCPConnections((prev) =>
        prev.filter((tc) => tc.id !== connection.id),
      );
      if (expandedMcpId === connection.id) setExpandedMcpId(null);
    } else {
      setTaskMCPConnections((prev) => [
        ...prev,
        {
          id: connection.id,
          enabled_tool_names: connection.tools.map((t) => t.name),
        },
      ]);
    }
  };

  const handleMcpExpansionToggle = (connectionId) =>
    setExpandedMcpId((p) => (p === connectionId ? null : connectionId));

  const handleMcpToolToggle = (connectionId, toolName) => {
    setTaskMCPConnections((prev) =>
      prev.map((conn) => {
        if (conn.id !== connectionId) return conn;
        const s = new Set(conn.enabled_tool_names);
        s.has(toolName) ? s.delete(toolName) : s.add(toolName);
        return { ...conn, enabled_tool_names: Array.from(s) };
      }),
    );
  };

  const handleOqlousAppToggle = (e, app) => {
    e.stopPropagation();
    const isAdded = taskOqlousTools.some((ta) => ta.appname === app.appname);
    if (isAdded) {
      setTaskOqlousTools((prev) =>
        prev.filter((ta) => ta.appname !== app.appname),
      );
      if (expandedAppName === app.appname) setExpandedAppName(null);
    } else {
      setTaskOqlousTools((prev) => [
        ...prev,
        {
          appname: app.appname,
          enabled_tool_names: app.tools.map((t) => t.name),
        },
      ]);
    }
  };

  const handleOqlousAppExpansionToggle = (appname) => {
    setExpandedAppName((p) => (p === appname ? null : appname));
  };

  const handleOqlousToolToggle = (appname, toolName) => {
    setTaskOqlousTools((prev) =>
      prev.map((app) => {
        if (app.appname !== appname) return app;
        const s = new Set(app.enabled_tool_names);
        s.has(toolName) ? s.delete(toolName) : s.add(toolName);
        return { ...app, enabled_tool_names: Array.from(s) };
      }),
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* eslint-disable-next-line */}
      <div
        className="fixed inset-0 z-[110] bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div className="fixed left-1/2 top-1/2 z-[120] flex max-h-[85vh] w-[90vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-zinc-700 bg-zinc-900 shadow-2xl">
        {/* ── Header ── */}
        <header className="flex flex-shrink-0 items-center justify-between border-b border-zinc-700 p-4">
          <div className="flex items-center gap-3">
            <CgPlug className="text-xl text-blue-400" />
            <h3 className="text-lg font-semibold text-white">
              Select Tools for Task
            </h3>
          </div>
          <div className="flex items-center gap-4">
            <ToggleSwitch
              enabled={taskManualToolSelection}
              onChange={() => setTaskManualToolSelection((p) => !p)}
            />
            <button
              onClick={onClose}
              className="rounded-full p-1 hover:bg-zinc-700"
            >
              <IoMdClose size={20} />
            </button>
          </div>
        </header>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto p-6">
          <div
            className={`space-y-5 transition-opacity duration-300 ${!taskManualToolSelection ? 'pointer-events-none opacity-40' : ''}`}
          >
            {isLoading ? (
              <p className="text-center text-gray-400">
                Loading tools and connections…
              </p>
            ) : (
              <>
                {/* ══ MCP CONNECTIONS ══ */}
                {ENABLE_MCP && validConnectionsToShow.length > 0 && (
                  <section>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-zinc-400">
                      MCP Connections
                    </h3>
                    <div className="space-y-2">
                      {validConnectionsToShow.map((conn) => {
                        const taskConn = taskMCPConnections.find(
                          (tc) => tc.id === conn.id,
                        );
                        const isEnabled = !!taskConn;
                        const isExpanded = expandedMcpId === conn.id;
                        return (
                          <div
                            key={conn.id}
                            className="rounded-lg border border-zinc-700 bg-zinc-800/50"
                          >
                            {/* app header row */}
                            {/* eslint-disable-next-line */}
                            <div
                              className={`flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-zinc-800 ${isExpanded ? 'border-b border-zinc-700' : ''}`}
                              onClick={() =>
                                isEnabled && handleMcpExpansionToggle(conn.id)
                              }
                            >
                              <p className="font-semibold text-white">
                                {conn.connection_name}
                              </p>
                              <div className="flex items-center gap-4">
                                <ToggleSwitch
                                  enabled={isEnabled}
                                  onChange={(e) =>
                                    handleMcpConnectionToggle(e, conn)
                                  }
                                />
                                {isEnabled && (
                                  <span className="text-gray-400">
                                    {isExpanded ? (
                                      <FaChevronUp />
                                    ) : (
                                      <FaChevronDown />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            {isEnabled && isExpanded && (
                              <div className="space-y-2 px-4 py-2">
                                {conn.tools.map((tool) => (
                                  <div
                                    key={tool.name}
                                    className="flex items-center justify-between"
                                  >
                                    <div>
                                      <p className="text-sm text-gray-200">
                                        {tool.name}
                                      </p>
                                      <p className="max-w-lg truncate text-xs text-gray-500">
                                        {tool.description}
                                      </p>
                                    </div>
                                    <ToggleSwitch
                                      enabled={
                                        taskConn?.enabled_tool_names.includes(
                                          tool.name,
                                        ) || false
                                      }
                                      onChange={() =>
                                        handleMcpToolToggle(conn.id, tool.name)
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* ══ SUPERMANAGER APP INTEGRATIONS ══ */}
                {validOqlousAppsToShow.length > 0 && (
                  <section>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-widest text-zinc-400">
                      SuperManager
                    </h3>
                    <div className="space-y-2">
                      {validOqlousAppsToShow.map((app) => {
                        const taskApp = taskOqlousTools.find(
                          (ta) => ta.appname === app.appname,
                        );
                        const isEnabled = !!taskApp;
                        const isExpanded = expandedAppName === app.appname;
                        const InlineConfig = INLINE_CONFIGS[app.appname];

                        return (
                          <div
                            key={app.appname}
                            className="rounded-lg border border-zinc-700 bg-zinc-800/50"
                          >
                            {/* app header row */}
                            {/* eslint-disable-next-line */}
                            <div
                              className={`flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-zinc-800 ${isExpanded ? 'border-b border-zinc-700' : ''}`}
                              onClick={() =>
                                isEnabled &&
                                handleOqlousAppExpansionToggle(app.appname)
                              }
                            >
                              <p className="font-semibold text-white">
                                {app.label || app.appname}
                              </p>
                              <div className="flex items-center gap-4">
                                <ToggleSwitch
                                  enabled={isEnabled}
                                  onChange={(e) =>
                                    handleOqlousAppToggle(e, app)
                                  }
                                />
                                {isEnabled && (
                                  <span className="text-gray-400">
                                    {isExpanded ? (
                                      <FaChevronUp />
                                    ) : (
                                      <FaChevronDown />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Config settings */}
                            <div
                              className={`${!isEnabled || !isExpanded ? 'hidden' : 'block'}`}
                            >
                              {InlineConfig && (
                                <div className="px-4 pt-3">
                                  <InlineConfig userGoogleId={userGoogleId} />
                                </div>
                              )}

                              {/* Divider + Tools label */}
                              {InlineConfig && (
                                <div className="px-4">
                                  <SectionDivider label="Tools" />
                                </div>
                              )}

                              {/* Tools */}
                              <div className="space-y-3 px-4 pb-4 pt-2">
                                {app.tools.map((tool) => (
                                  <div
                                    key={tool.name}
                                    className="flex items-center justify-between"
                                  >
                                    <div>
                                      <p className="text-sm text-gray-200">
                                        {tool.name}
                                      </p>
                                      <p className="max-w-lg truncate text-xs text-gray-500">
                                        {tool.description}
                                      </p>
                                    </div>
                                    <ToggleSwitch
                                      enabled={
                                        taskApp?.enabled_tool_names.includes(
                                          tool.name,
                                        ) || false
                                      }
                                      onChange={() =>
                                        handleOqlousToolToggle(
                                          app.appname,
                                          tool.name,
                                        )
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* ══ ATTENTION REQUIRED ══ */}
                {(invalidatedConnectionsToShow.length > 0 ||
                  invalidatedOqlousAppsToShow.length > 0) && (
                  <section>
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-yellow-400">
                      <FiAlertTriangle /> Attention Required
                    </h4>
                    <p className="mb-3 text-xs text-gray-400">
                      These items were selected for this task but are now
                      invalid or disabled.
                    </p>
                    <div className="space-y-2">
                      {invalidatedOqlousAppsToShow.map((app) => (
                        <div
                          key={app.appname}
                          className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-900/20 p-3"
                        >
                          <div>
                            <p className="font-semibold text-white line-through">
                              {app.appname}
                            </p>
                            <p className="text-xs text-red-400">
                              {app.errorMessage}
                            </p>
                          </div>
                          <ToggleSwitch
                            enabled={true}
                            onChange={(e) => handleOqlousAppToggle(e, app)}
                          />
                        </div>
                      ))}
                      {ENABLE_MCP &&
                        invalidatedConnectionsToShow.map((conn) => (
                          <div
                            key={conn.id}
                            className="flex items-center justify-between rounded-lg border border-red-500/30 bg-red-900/20 p-3"
                          >
                            <div>
                              <p className="font-semibold text-white line-through">
                                {conn.connection_name}
                              </p>
                              <p className="text-xs text-red-400">
                                {conn.errorMessage}
                              </p>
                            </div>
                            <ToggleSwitch
                              enabled={true}
                              onChange={(e) =>
                                handleMcpConnectionToggle(e, conn)
                              }
                            />
                          </div>
                        ))}
                    </div>
                  </section>
                )}

                {validConnectionsToShow.length === 0 &&
                  validOqlousAppsToShow.length === 0 &&
                  invalidatedConnectionsToShow.length === 0 &&
                  invalidatedOqlousAppsToShow.length === 0 && (
                    <p className="text-center text-gray-500">
                      No tools or connections found. Add integrations or MCP
                      connections first.
                    </p>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default McpTaskSelector;
